import './error-template.scss';

import React, { useEffect, useRef } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { NavLink, useLocation } from 'react-router';

import { ROUTES } from '@/components/Shared/routes/routes';
import i18next from '@/i18n';
import notFoundImage from '@/media/images/not-found.jpg';
import oopsErrorImage from '@/media/images/oops-error.jpg';

interface IProps {
  type: ErrorType;
  redirect?: {
    text: string;
    href: string;
  } | null;
}

const ERRORS: Record<ErrorType, ErrorsInterface> = {
  '404': {
    image: notFoundImage,
    title: i18next.t('404.title-1', { defaultValue: 'Страница не найдена' }),
    description: [
      i18next.t('404.description-1', {
        defaultValue: 'Такой страницы либо уже нет, либо еще нет.'
      }),
      i18next.t('404.description-2', {
        defaultValue: 'А может быть, вы перешли по неправильной ссылке.'
      })
    ]
  },
  oops: {
    image: oopsErrorImage,
    title: i18next.t('404.title-2', { defaultValue: 'Что-то пошло не так' }),
    description: [
      i18next.t('404.description-3', {
        defaultValue: 'Просим перезагрузить страницу или'
      })
    ]
  }
};

const ErrorTemplate: React.FC<IProps> = ({
  type,
  redirect = {
    text: 'Перейти на главную',
    href: ROUTES.autoCredit
  }
}) => {
  const location = useLocation();
  const { resetBoundary } = useErrorBoundary();
  const errorPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    if (errorPathnameRef.current === null) {
      errorPathnameRef.current = location.pathname;
      return;
    }
    if (errorPathnameRef.current !== location.pathname) {
      errorPathnameRef.current = location.pathname;
      resetBoundary();
    }
  }, [location, resetBoundary]);

  return (
    <div className="error-template">
      <img
        className="error-template__image"
        src={ERRORS[type].image}
        alt={`${type}-image`}
      />

      <p className="error-template__title">{ERRORS[type].title}</p>
      <p className="error-template__description">
        {ERRORS[type].description.map((item) => (
          <React.Fragment key={item}>
            {item} <br />
          </React.Fragment>
        ))}
        {redirect && (
          <NavLink className="error-template__link" to={redirect.href}>
            {redirect.text}
          </NavLink>
        )}
      </p>
    </div>
  );
};

export default ErrorTemplate;

type ErrorType = '404' | 'oops';

interface ErrorsInterface {
  image: string;
  title: string;
  description: string[];
}
